<script lang="ts">
	import { replaceState } from '$app/navigation';
	import { page } from '$app/state';
	import { ALL_CONTINENTS, CONTINENT_LABELS, type Continent } from '$lib/api/velatlas/response.types';
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import HeroImageMd from '$lib/assets/page/countries/vintage-camper-van-desert-highway-travel-md.avif';
	import HeroImageSm from '$lib/assets/page/countries/vintage-camper-van-desert-highway-travel-sm.avif';
	import CountryCard from '$lib/components/ui/CountryCard.svelte';
	import HeroImage from '$lib/components/ui/HeroImage.svelte';
	import type { ThumbnailCountry } from '$lib/features/country/countryThumbnail.type';
	import { ANALYTICS_EVENT_NAMES, FULL_SITE_URL, SITE_NAME } from '$lib/statics';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import type { Snapshot } from '@sveltejs/kit';
	import { watch } from 'runed';
	import { onDestroy, onMount, tick } from 'svelte';
	import { flip } from 'svelte/animate';
	import { fade, fly } from 'svelte/transition';
	import IconEarth from '~icons/mdi/earth';
	import IconLanguage from '~icons/mdi/language';
	import IconLightbulb from '~icons/mdi/lightbulb';
	import IconRemove from '~icons/mdi/remove';
	import IconSearch from '~icons/mdi/search';
	import type { Output } from './+page.server';

	const { data }: { data: { output: Output } } = $props();

	const analyticsApi = new ClientVelatlasLoggerApi();
	let debounceTimeout: ReturnType<typeof setTimeout>;

	let searchQuery = $state<string>('');
	let selectedContinents = $state<Continent[]>([]);

	let filteredCountries = $derived<ThumbnailCountry[]>(
		data.output.countries.filter((item) => {
			if (selectedContinents.length > 0 && !selectedContinents.includes(item.continent)) {
				return false;
			}
			const lowerCaseSearchQuery = searchQuery.toLowerCase();
			return (
				item.englishName.toLowerCase().includes(lowerCaseSearchQuery) ||
				(item.nativeName?.toLowerCase().includes(lowerCaseSearchQuery) ?? false)
			);
		})
	);
	const jsonLdCountriesCollection = $derived.by(() => {
		const json = data.output.jsonLdCountriesCollection;
		json.name = meta.title;
		json.description = meta.description;
		return json;
	});
	const combinedJsonLd = $derived(jsonLdCountriesCollection);

	function clearFilters(): void {
		searchQuery = '';
		selectedContinents = [];
	}

	// we keep a snapshot so navigation back and forth works as expected, especially in terms of URL params
	export const snapshot: Snapshot<{
		filteredCountries: ThumbnailCountry[];
		searchQuery: string;
		selectedContinents: Continent[];
	}> = {
		capture: () => {
			return {
				filteredCountries,
				searchQuery,
				selectedContinents
			};
		},
		restore: (value) => {
			filteredCountries = value.filteredCountries;
			searchQuery = value.searchQuery;
			selectedContinents = value.selectedContinents;
		}
	};

	onMount(() => {
		const params = page.url.searchParams;
		if (params.has('q')) {
			searchQuery = params.get('q')!;
		}
	});

	onDestroy(() => {
		clearTimeout(debounceTimeout);
	});

	$effect(() => {
		const target = UrlUtil.toCountries({ query: searchQuery });
		// need this tick, otherwise an error stating the router isn't initialised yet occurs
		tick().then(() => {
			replaceState(target.href, {});
		});
	});

	watch(
		() => filteredCountries,
		(curr) => {
			clearTimeout(debounceTimeout);
			const cooldownDelayMs = 850;
			debounceTimeout = setTimeout(() => {
				const queryLength = searchQuery.trim().length;
				const searchProducedNoData = curr.length === 0 && queryLength > 0;
				const queryLengthHasMinChars = queryLength > 3;
				if (searchProducedNoData || queryLengthHasMinChars) {
					analyticsApi.sendAnalytics({
						event: ANALYTICS_EVENT_NAMES.emptySearch,
						payload: { query: searchQuery.toLowerCase().trim() }
					});
				}
			}, cooldownDelayMs);
		}
	);

	const meta = {
		title: `Explore Countries - ${SITE_NAME}`,
		description:
			'Browse countries, discover and learn about your next travel destination. Filter by continent and find the best cities to visit.',
		canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCountries().href}`,
		imageUrl: UrlUtil.getStaticFileFullUrl('images', 'scenic-coastal-city-travel-destinations-brazil-banner.jpg')
	};
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<div class="flex min-h-screen flex-col gap-15 text-base-content">
	<section class="hero min-h-[65vh] overflow-hidden">
		<div class="group relative h-full w-full overflow-hidden">
			{@render Hero()}
		</div>
	</section>
	<section class="container mx-auto px-4">
		{@render Results()}
	</section>
	<section class="container mx-auto max-w-4xl px-4">
		{@render ExtraHelp()}
	</section>
</div>

{#snippet Hero()}
	<HeroImage
		mdImage={HeroImageMd}
		smImage={HeroImageSm}
		size={{ width: 1800, height: 1273 }}
		altText="Yellow Volkswagen van on a scenic road trip, representing travel adventures and exploring natural attractions outside the city"
	/>

	<div class="relative z-10 container mx-auto flex h-full flex-col px-4 text-center text-white">
		<div class="min-h-20 shrink-0 basis-[15vh]"></div>

		<div class="flex grow flex-col items-center justify-center gap-6">
			<header class="flex flex-col gap-3">
				<h1 class="text-4xl leading-none drop-shadow-lg md:text-6xl">
					Browse Destinations <br />
					<span class="bg-linear-to-r from-white to-primary bg-clip-text text-transparent"> by Country </span>
				</h1>
				<p class="max-w-2xl text-lg text-white/80">
					Browse our directory of global nations. Discover regional histories, primary spoken languages, and key urban centers.
				</p>
			</header>

			<div class="group relative mx-auto w-full max-w-lg">
				{@render SearchBar()}
			</div>
		</div>

		<div class="container mx-auto mt-8 mb-8 max-w-3xl">
			{@render ContinentChips()}
		</div>
	</div>

	{#snippet SearchBar()}
		<label class="input w-full rounded-full bg-base-100 text-base-content shadow-md">
			<IconSearch class="text-base-content/75" />
			<input
				name="page-countries-search-input"
				type="search"
				placeholder="Search countries (e.g. Australia, Italia)..."
				bind:value={searchQuery}
			/>
		</label>
	{/snippet}

	{#snippet ContinentChips()}
		<form
			class="flex flex-wrap justify-center gap-2 rounded-box bg-base-100/95 px-4 py-3 shadow-md backdrop-blur-md transition-all md:mt-0 md:justify-center md:rounded-full md:px-6"
			name="continent-chips-form"
			aria-label="Filter countries by continent"
		>
			{#each ALL_CONTINENTS as continent (continent)}
				<input
					class="btn rounded-full btn-outline transition-all duration-300 btn-primary btn-sm"
					type="checkbox"
					name="continents"
					aria-label={CONTINENT_LABELS[continent]}
					value={continent}
					bind:group={selectedContinents}
				/>
			{/each}

			<button
				class="btn btn-circle btn-ghost transition-all duration-300 btn-error btn-sm disabled:opacity-0"
				type="reset"
				aria-label="Clear filters"
				disabled={selectedContinents.length === 0}
			>
				<IconRemove />
			</button>
		</form>
	{/snippet}
{/snippet}

{#snippet Results()}
	{const allCountriesCount = $derived<number>(data.output.countries.length)}
	{const noFiltersApplied = $derived<boolean>(filteredCountries.length === allCountriesCount)}
	<div class="mb-6 flex items-end justify-between border-b border-base-content/10 pb-4">
		<h2 class="text-2xl">Countries</h2>
		<span class="text-sm font-medium text-base-content/70">
			{#if noFiltersApplied}
				{filteredCountries.length} results
			{:else}
				{filteredCountries.length} / {allCountriesCount} results
			{/if}
		</span>
	</div>

	{#if filteredCountries.length > 0}
		<ul class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{#each filteredCountries as country, i (country.code)}
				<li animate:flip={{ duration: 300 }} in:fly={{ y: 20, duration: 300 }} out:fade={{ duration: 200 }}>
					<CountryCard {country} eagerLoadCoverImage={i < 4} />
				</li>
			{/each}
		</ul>
	{:else}
		{@render EmptyState()}
	{/if}

	{#snippet EmptyState()}
		<div class="flex flex-col items-center justify-center py-20" in:fade>
			<IconEarth class="mb-4 text-6xl opacity-20" />
			<h3 class="mb-2 text-xl">No countries found</h3>
			<p class="mx-auto max-w-xs text-base-content/70">Try adjusting your search or filters to find what you're looking for.</p>
			<button class="btn mt-6 rounded-full btn-outline btn-primary" onclick={clearFilters}> Clear all filters </button>
		</div>
	{/snippet}
{/snippet}

{#snippet ExtraHelp()}
	<div class="mb-12 text-center">
		<h2 class="mb-6 text-3xl md:text-4xl">A World of Possibilities</h2>
		<p class="mx-auto max-w-2xl text-lg leading-relaxed text-base-content/70">
			The world is vast, but planning your trip doesn't have to be overwhelming. Start by narrowing down your search by
			<strong class="text-base-content">Continent</strong>, or explore destinations based on the
			<strong class="text-base-content">Cultural Experience</strong> you're craving.
		</p>
	</div>

	<div class="mt-12 grid gap-8 md:grid-cols-2">
		<div>
			<div class="mb-3 flex items-center gap-3">
				<div class="rounded-lg bg-warning/10 p-2">
					<IconEarth class="text-xl text-warning" />
				</div>
				<h3 class="text-xl">Explore by Continent</h3>
			</div>
			<p class="text-sm leading-relaxed text-base-content/70">
				Geography defines each journey. Use our filters to switch between the historical architecture of
				<strong class="text-base-content">Europe</strong>, the diverse climates of
				<strong class="text-base-content">America</strong>, or the dense metropolitan hubs of
				<strong class="text-base-content">Asia</strong>. Each region presents a distinct cultural experience and travel pace.
			</p>
		</div>
		<div>
			<div class="mb-3 flex items-center gap-3">
				<div class="rounded-lg bg-success/10 p-2">
					<IconLanguage class="text-xl text-success" />
				</div>
				<h3 class="text-xl">Language & Culture</h3>
			</div>
			<p class="text-sm leading-relaxed text-base-content/70">
				Language facilitates local interaction, and speaking the local tongue often opens doors to more welcoming encounters. We
				list the primary
				<strong class="text-base-content">Languages</strong> spoken in each country to assist in your preparation. Practicing
				regional dialects in
				<strong class="text-base-content">Africa</strong> or learning basic Spanish before traveling to
				<strong class="text-base-content">South America</strong> provides practical support during transit.
			</p>
		</div>
	</div>

	<div class="mt-12 rounded-box border border-base-content/5 bg-base-200 p-8">
		<div class="mb-3 flex items-center gap-2 text-lg">
			<IconLightbulb />
			<h3>Traveler's Note</h3>
		</div>
		<p class="text-sm leading-relaxed text-base-content/80">
			Not sure where to start? Each country card links to a directory of its documented cities. These city profiles help you
			evaluate if a destination is best suited for a brief weekend stay or a longer travel itinerary.
		</p>
	</div>
{/snippet}
